import { AlertTriangle, RotateCcw } from "lucide-react";
import { fmtTs } from "./PipelineTimeline";

const STAGE_LABEL = {
  cibil: "CIBIL Approval",
  legal: "Legal Approval",
  tech:  "Tech Approval",
  ok:    "Bank Approval",
};

/**
 * Renders previous rejections for a lead. Shows nothing when `lead.rejections`
 * is empty. When `compact` is true, renders a tighter chip-style row (used on
 * list cards). Otherwise renders a detailed block (used in modals).
 */
export default function RejectionHistory({ lead, compact = false }) {
  const rejections = Array.isArray(lead?.rejections) ? lead.rejections : [];
  if (!rejections.length) return null;

  if (compact) {
    return (
      <div className="mt-2 rounded-md border border-red-500/40 bg-red-500/10 px-2.5 py-1.5 text-[11px] text-red-200"
           data-testid={`rej-compact-${lead.lead_id}`}>
        <div className="flex items-center gap-1.5 font-bold uppercase tracking-widest text-[10px]">
          <RotateCcw className="w-3 h-3"/> Previously Rejected · {rejections.length}
        </div>
        <div className="mt-1 space-y-0.5">
          {rejections.slice(-2).map((r, i) => (
            <div key={i} className="truncate">
              <span className="font-semibold">{STAGE_LABEL[r.stage] || (r.stage || "").toUpperCase()}</span>
              {r.bank_name && <span className="opacity-80"> · {r.bank_name}</span>}
              {r.reason && <span className="opacity-70"> — {r.reason}</span>}
            </div>
          ))}
          {rejections.length > 2 && (
            <div className="opacity-70 text-[10px]">+{rejections.length - 2} more — open case for details</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3" data-testid={`rej-block-${lead.lead_id}`}>
      <div className="flex items-center gap-2 text-red-300 font-bold text-xs uppercase tracking-widest mb-2">
        <AlertTriangle className="w-3.5 h-3.5"/> Previously Rejected — {rejections.length} time{rejections.length === 1 ? "" : "s"}
      </div>
      <div className="text-[11px] text-red-200/80 mb-2 italic">
        Different bank policies may still allow this case. Read the reason(s) before taking.
      </div>
      <div className="space-y-2">
        {rejections.map((r, i) => (
          <div key={i} className="rounded-md bg-slate-950/40 border border-red-500/20 p-2 text-xs" data-testid={`rej-item-${i}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-red-200">
              <span className="font-bold uppercase tracking-widest text-[10px] text-red-300">
                {STAGE_LABEL[r.stage] || (r.stage || "").toUpperCase()} — REJECTED
              </span>
              <span className="text-slate-400 font-mono-alt text-[10px]">{fmtTs(r.at)}</span>
            </div>
            <div className="mt-1 text-slate-300">
              <span className="text-slate-500">By </span>
              <span className="text-white font-semibold">{r.by_name || "Bank Staff"}</span>
              {r.bank_name && (<>
                <span className="text-slate-500"> · Bank </span>
                <span className="text-white font-semibold">{r.bank_name}</span>
              </>)}
            </div>
            {r.reason && (
              <div className="mt-1 text-slate-300">
                <span className="text-slate-500">Reason: </span>"{r.reason}"
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
